import { OrderCustomInput, OrderInputItem } from '../../../../clients/quipli-service/order/order-calculation';
import { Location } from '../../../../clients/quipli-service/location/location';
import { useProductApiV1 } from '../../../../clients/quipli-service/product/product-api-v1';
import { usePricingApiV1 } from '../../../../clients/quipli-service/pricing/pricing-api-v1';
import { useAccountingApiV1 } from '../../../../clients/quipli-service/accounting/accounting-api-v1';
import { useTaxApiV1 } from '../../../../clients/quipli-service/tax/tax-api-v1';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { useEffect, useState } from 'react';
import { Product } from '../../../../clients/quipli-service/product/product';
import {
  CalculateRentalPriceResult,
  CustomOrderPriceRequest,
  CustomProductPricingConfig,
  RentalProductPrice,
} from '../../../../clients/quipli-service/pricing/pricing';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemType,
} from '../../../../clients/quipli-service/accounting/accounting';
import { TaxCategory } from '../../../../clients/quipli-service/tax/tax';
import { DateTime, Duration, Interval } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { toast } from '../../../../util/toasts';
import { Radio } from '../../../../components/radio/radio';
import { EditableField, Field, FieldLabel } from '../../../../components/field/field';
import { DateTimePicker } from '../../../../components/date-picker/date-time-picker';
import { displayMoneyFormat, safeRound } from '../../../../util/util';
import { Typography } from '../../../../components/typography/typography';
import { QuantityInput } from '../../../../customer/product-form/quantity-input/quantity-input';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { Button } from '../../../../components/form/button';

export const CustomProductForm = ({
  isCycleBilled,
  cycleLength = Duration.fromObject({ days: 28 }),
  onSubmit,
  onCollapse,
  location,
  customOrderRentalRatesEnabled,
}: {
  isCycleBilled: boolean;
  cycleLength?: Duration;
  onSubmit?: ({ input }: { input: OrderInputItem }) => void;
  onCollapse?: () => void;
  location: Location;
  customOrderRentalRatesEnabled: boolean;
}) => {
  const { getCustomProduct } = useProductApiV1();
  const { listCustomProductPricingConfigs } = usePricingApiV1();
  const { listAccounts, getItemGeneralLedgerAccount } = useAccountingApiV1();
  const { searchTaxCategories } = useTaxApiV1();
  const { calculatePriceAsAdmin } = useAPI();

  const [customProduct, setCustomProduct] = useState<Product>();
  const [customProductPricingConfigs, setCustomProductPricingConfigs] = useState<CustomProductPricingConfig[]>([]);
  const [customRentalProductPrices, setCustomRentalProductPrices] = useState<RentalProductPrice[]>([]);
  const [orderType, setOrderType] = useState<OrderType>(OrderType.Sale);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [generalLedgerAccount, setGeneralLedgerAccount] = useState<GeneralLedgerAccount | undefined>();
  const [defaultGeneralLedgerAccount, setDefaultGeneralLedgerAccount] = useState<GeneralLedgerAccount | undefined>();
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>();
  const [taxCategory, setTaxCategory] = useState<TaxCategory | undefined>();
  const [price, setPrice] = useState<number | undefined>();
  const [qty, setQty] = useState<number>(1);
  const [taxable, setTaxable] = useState<boolean>(true);
  const [startDateTime, setStartDateTime] = useState<DateTime | undefined>();
  const [endDateTime, setEndDateTime] = useState<DateTime | undefined>();
  const [customRentalRatesForCalc, setCustomRentalRatesForCalc] = useState<{
    [durationStr: string]: CustomOrderPriceRequest | undefined;
  }>({});
  const [calculatedPriceResult, setCalculatedPriceResult] = useState<CalculateRentalPriceResult>();
  const [equipmentOwner, setEquipmentOwner] = useState<string>();

  const isDisabled =
    !customProduct ||
    !name ||
    price === undefined ||
    !qty ||
    (orderType === OrderType.Rental &&
      (!startDateTime ||
        !endDateTime ||
        !calculatedPriceResult ||
        !Object.values(customRentalRatesForCalc).every((v) => v !== undefined)));

  const submit = () => {
    if (isDisabled) return;

    let input: OrderCustomInput;
    switch (orderType) {
      case OrderType.Sale:
        input = {
          id: uuidv4(),
          type: OrderType.Sale,
          productId: customProduct!.id,
          isCustom: true,
          quantity: qty,
          name,
          description,
          customPrice: safeRound(price * 100),
          taxable,
          generalLedgerAccountId: generalLedgerAccount?.id,
          taxCategoryId: taxCategory?.id,
        };
        break;
      case OrderType.Rental:
        input = {
          id: uuidv4(),
          type: OrderType.Rental,
          productId: customProduct!.id,
          isCustom: true,
          quantity: qty,
          name,
          description,
          customPrice: safeRound(price * 100),
          taxable,
          generalLedgerAccountId: generalLedgerAccount?.id,
          taxCategoryId: taxCategory?.id,
          start: startDateTime!,
          end: endDateTime!,
          customOrderRentalRates: Object.values(customRentalRatesForCalc).filter(
            (r) => !!r,
          ) as CustomOrderPriceRequest[],
          equipmentOwner,
        };
        break;
    }

    onSubmit && onSubmit({ input });
    toast('Custom product added to order.');

    clear();
  };

  function onTaxCategoryChange(selected?: { label: string; value: string }) {
    if (selected) {
      setTaxCategory(taxCategories?.find((a) => a.id === selected.value));
    } else {
      setTaxCategory(undefined);
    }
  }

  const clear = () => {
    setName('');
    setDescription('');
    setQty(1);
    setGeneralLedgerAccount(defaultGeneralLedgerAccount);
    setCustomRentalRatesForCalc({});
    setPrice(undefined);
    setTaxable(true);
  };

  const buildCustomRentalProductPrices = (customProductPricingConfigs: CustomProductPricingConfig[]) => {
    const basePriceOption = {
      id: uuidv4(),
      productId: customProduct?.id ?? 0,
      price: price ?? 0,
      type: OrderType.Rental,
      featured: false,
      createdAt: DateTime.now(),
      locationIds: undefined,
    };

    const customPriceOptions: RentalProductPrice[] = [];

    const addPriceOption = (config: CustomProductPricingConfig, durationMinutes: number, durationLabel: string) => {
      customPriceOptions.push({
        ...basePriceOption,
        type: OrderType.Rental,
        durationValue: config.durationValue,
        durationUnit: config.durationUnit,
        durationMinutes: durationMinutes * config.durationValue,
        durationLabel: `Per ${durationLabel}${config.durationValue > 1 ? 's' : ''}`,
      } as RentalProductPrice);
    };

    // We want the pricing options to be sorted in ascending order by unit an value,
    // so we have to manually add them in the correct order

    // Add hourly pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'hour')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 60, 'Hour');
      });

    // Add daily pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'day')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 1440, 'Day');
      });

    // Add weekly pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'week')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 10080, 'Week');
      });

    // Add monthly pricing
    customProductPricingConfigs
      .filter((c) => c.durationUnit === 'month')
      .sort((c) => c.durationValue)
      .map((config) => {
        addPriceOption(config, 43200, 'Month');
      });

    return customPriceOptions;
  };

  useEffect(() => {
    Promise.all([
      getCustomProduct().then(setCustomProduct),
      listCustomProductPricingConfigs().then(setCustomProductPricingConfigs),
      searchTaxCategories({}).then(setTaxCategories),
      listAccounts(false, GeneralLedgerAccountType.Income).then((res) => {
        setAccounts(res);
        getItemGeneralLedgerAccount(ItemType.CustomProduct).then((item) => {
          setGeneralLedgerAccount(res.find((a) => a.id === item?.generalLedgerAccountId));
          setDefaultGeneralLedgerAccount(res.find((a) => a.id === item?.generalLedgerAccountId));
        });
      }),
    ]);
  }, []);

  useEffect(() => {
    setCustomRentalProductPrices(buildCustomRentalProductPrices(customProductPricingConfigs));
  }, [customProductPricingConfigs]);

  useEffect(() => {
    if (startDateTime && endDateTime && Object.keys(customRentalRatesForCalc).length && customProduct) {
      const maxEndDate = startDateTime.plus(cycleLength);
      calculatePriceAsAdmin({
        productId: customProduct?.id,
        type: OrderType.Rental,
        start: startDateTime,
        end: isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime,
        customOrderRentalRates: Object.values(customRentalRatesForCalc).filter((r) => !!r) as CustomOrderPriceRequest[],
      }).then((res) => {
        setCalculatedPriceResult(res as CalculateRentalPriceResult);
        setPrice((res as CalculateRentalPriceResult).totalPrice / 100);
      });
    }
  }, [startDateTime, endDateTime, customRentalRatesForCalc, isCycleBilled, cycleLength]);

  return (
    <>
      <div className="flex gap-4">
        <Radio label="Sale" isChecked={orderType === OrderType.Sale} onChange={() => setOrderType(OrderType.Sale)} />
        <Radio
          label="Rental"
          isChecked={orderType === OrderType.Rental}
          onChange={() => setOrderType(OrderType.Rental)}
        />
      </div>
      <Field label="Name" isRequired type="text" value={name} onChange={(v) => setName(v)} dataCy="custom_field_name" />

      <Field
        label="Description"
        type="text"
        value={description}
        onChange={(v) => setDescription(v)}
        dataCy="custom_field_description"
      />

      {orderType === OrderType.Rental && (
        <>
          <Field
            label="Equipment Owner"
            type="text"
            value={equipmentOwner}
            onChange={(v) => setEquipmentOwner(v)}
            dataCy="custom_field_equipment_owner"
          />

          <div className="flex flex-col md:flex-row gap-y-[16px] gap-x-[24px]">
            <div className="flex-grow">
              <DateTimePicker
                label="Rent from"
                value={startDateTime}
                onChange={setStartDateTime}
                timezone={location?.timezone}
                usePortal
                data-cy="product_start_date"
              />
            </div>

            <div className="flex-grow">
              <DateTimePicker
                label="Rent to"
                value={endDateTime}
                onChange={setEndDateTime}
                timezone={location?.timezone}
                disabledIntervals={[
                  Interval.before(startDateTime?.plus({ minutes: 30 }) || DateTime.now(), { years: 100 }),
                ]}
                usePortal
                clearable={false}
                data-cy="product_end_date"
              />
            </div>
          </div>
        </>
      )}

      {orderType === OrderType.Rental && customOrderRentalRatesEnabled && (
        <table className={'invoice-table min-w-full'}>
          <thead>
            <tr data-cy="rental_prices_table_headers">
              {customRentalProductPrices.map((price: RentalProductPrice) => {
                return (
                  <th className="text-left">
                    {`Per ${price.durationValue > 1 ? `${price.durationValue} ` : ''}${price.durationUnit}${
                      price.durationValue > 1 ? 's' : ''
                    }`}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr data-cy="rental_prices_table_prices_row">
              {customRentalProductPrices.map((price: RentalProductPrice) => {
                return (
                  <td key={`rental-price-body-${price.durationValue}-${price.durationUnit}`} className="p-2">
                    <EditableField
                      type="number"
                      value={
                        customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price
                          ? customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]!.price / 100
                          : undefined
                      }
                      formattedValue={
                        customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]
                          ? displayMoneyFormat(
                              (customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price ??
                                price.price) / 100,

                              location?.currency,
                            )
                          : '--'
                      }
                      canReset={
                        !!customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`] &&
                        customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price !== price.price
                      }
                      resetPosition="right"
                      onReset={() => {
                        setCustomRentalRatesForCalc({
                          ...customRentalRatesForCalc,
                          [`${price.durationValue}-${price.durationUnit}`]: undefined,
                        });
                      }}
                      onChange={(v) => {
                        setCustomRentalRatesForCalc({
                          ...customRentalRatesForCalc,
                          [`${price.durationValue}-${price.durationUnit}`]: {
                            ...price,
                            price: v * 100,
                            action:
                              v * 100 === price.originalPrice?.price
                                ? 'remove'
                                : price.originalPrice?.price
                                ? 'update'
                                : 'add',
                          },
                        });
                      }}
                    />
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      )}

      {!!calculatedPriceResult && (
        <div className="flex-[calc(50%_-_(24px_/_2))] flex-grow-0 rounded-[4px] bg-grey-50 px-[4px] py-[6px]">
          {calculatedPriceResult.componentPrices &&
            calculatedPriceResult.componentPrices.map((componentPrice, index) => {
              return (
                <Typography key={`component-price-${index}`} type="text-sm" className="text-grey-500">
                  {safeRound(componentPrice.consumedUnits * 1000) / 1000} x{' '}
                  {displayMoneyFormat(componentPrice.unitPrice.price / 100, location?.currency)}{' '}
                  {`Per ${componentPrice.unitPrice.durationValue} ${componentPrice.unitPrice.durationUnit}${
                    componentPrice.unitPrice.durationValue > 1 ? 's' : ''
                  }`}
                  {index === calculatedPriceResult.componentPrices.length - 1 && (
                    <>{` = ${displayMoneyFormat(calculatedPriceResult.totalPrice / 100, location?.currency)}`}</>
                  )}
                </Typography>
              );
            })}
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-[12px] justify-between mb-3">
        <div className="basis-[calc(50%_-_12px)]">
          {orderType === OrderType.Sale ? (
            <Field
              label="Price"
              isRequired
              type="currency"
              value={price}
              onChange={(v) => {
                if (Number.isNaN(v) && !price) {
                  setPrice(NaN);
                } else setPrice(v);
              }}
              maximumFractionDigits={2}
              dataCy="custom_field_price"
            />
          ) : (
            <></>
          )}
        </div>

        <div className="">
          <FieldLabel label="Quantity" />
          <QuantityInput name="Quantity" value={`${qty}`} onChange={(v) => setQty(parseInt(v ?? '1'))} />
        </div>
      </div>

      <Field
        type="dropdown"
        label="General Ledger Account"
        value={{
          label: generalLedgerAccount?.name,
          value: generalLedgerAccount?.id,
        }}
        dropdownProps={{ menuPortalTarget: document.body }}
        onChange={(v) => setGeneralLedgerAccount(accounts?.find((a) => a.id === v.value))}
        options={accounts?.map((a) => ({ label: a.name, value: a.id }))}
        dataCy="custom_field_gla"
      />

      {taxCategories && taxCategories.length > 0 && (
        <>
          <FieldLabel label="Tax Category" />
          <Field
            type="dropdown"
            value={{
              label: taxCategory?.name,
              value: taxCategory?.id,
            }}
            onChange={(a) => onTaxCategoryChange(a)}
            options={taxCategories?.map((a) => ({ label: a.name, value: a.id }))}
            isClearable={true}
          />
        </>
      )}

      <Checkbox
        label="Taxable"
        type="white"
        isChecked={taxable}
        onChange={(v) => setTaxable(v)}
        dataCy="custom_field_taxable"
      />

      <div className="button-group">
        <Button
          className="button-secondary"
          onClick={() => {
            onCollapse && onCollapse();
            clear();
          }}
          data-cy="cancel_add_product_to_order"
        >
          Cancel
        </Button>
        <Button className="button-primary" onClick={submit} disabled={isDisabled} data-cy="add_product_to_order">
          Add to order
        </Button>
      </div>
    </>
  );
};
